import { EllipsisVertical } from 'lucide-react';

import { useTiclickTableContext } from '@src/design-system/molecules/ticlick-table/context/ticlick-table-context-hook';
import customFieldApi from '@src/shared/features/custom-fields/services/custom-field-api';
import type { TCustomField } from '@src/shared/types/custom-field';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@src/design-system/dropdown-menu';
import { Button } from '@src/design-system/button';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '@src/design-system/dialog';
import { CustomFieldAddDialog } from './custom-field-add-dialog';
import { DeleteConfirmDialog } from '@src/design-system/molecules/delete-confirm-dialog-button';

export const CustomFieldItemActions = ({
  customField,
}: {
  customField: TCustomField;
}) => {
  return (
    <Dialog>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size='iconXs'
          >
            <EllipsisVertical className='size-4' />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align='end'
          className='text-sm'
        >
          <CustomFieldItemEditAction customField={customField} />
          <CustomFieldItemDeleteAction id={customField.id} />
        </DropdownMenuContent>
      </DropdownMenu>
    </Dialog>
  );
};

const CustomFieldItemDeleteAction = ({ id }: { id: number }) => {
  const { mutateAsync: deleteCustomField, isPending } =
    customFieldApi.useDelete();
  const { table } = useTiclickTableContext();

  return (
    <DropdownMenuItem
      onClick={(e) => e.preventDefault()}
      asChild
    >
      <Dialog>
        <DialogTrigger className='text-destructive text-left rounded-md px-2 cursor-pointer h-[32px] hover:bg-destructive/5 w-[80px]'>
          Delete
        </DialogTrigger>

        <DialogContent align='center'>
          <DeleteConfirmDialog
            title='Are you sure you want to delete this item?'
            onConfirm={(closeModal) => {
              deleteCustomField({ id }).then(() => {
                table?.options.meta?.queryData?.refetch();
                closeModal();
              });
            }}
            isLoading={isPending}
            confirmText='Delete'
            cancelText='Cancel'
          />
        </DialogContent>
      </Dialog>
    </DropdownMenuItem>
  );
};

const CustomFieldItemEditAction = ({
  customField,
}: {
  customField: TCustomField;
}) => {
  return (
    <>
      <DropdownMenuItem
        className='h-[32px] w-[80px]'
        onClick={(e) => e.preventDefault()}
      >
        <DialogTrigger className='absolute inset-0 px-2 text-left cursor-pointer'>
          Edit
        </DialogTrigger>
      </DropdownMenuItem>

      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        dialogOverlayProps={{
          className: 'bg-transparent',
        }}
      >
        <CustomFieldAddDialog customField={customField} />
      </DialogContent>
    </>
  );
};
